import { ShieldOff, Server, Cpu, CheckCircle2, XCircle } from 'lucide-react'
import type { CoachingContext } from '@/types/coaching'
import type { LLMProviderInfo } from '@/hooks/useLLMProvider'

interface DataPostureViewProps {
  context: CoachingContext | undefined
  llm: LLMProviderInfo | undefined
  excludedCount: number
}

export function DataPostureView({ context, llm, excludedCount }: DataPostureViewProps) {
  const priorityCount = context?.priorities.length ?? 0
  const themeCount = context?.journalThemes.length ?? 0

  return (
    <div className="space-y-6 text-sm">

      {/* Live snapshot */}
      <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-3">
        <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
          Current snapshot
        </p>
        <ul className="space-y-1.5 text-sm">
          <li className="flex items-center gap-2">
            <CheckCircle2 size={13} className="text-emerald-500 flex-shrink-0" />
            <span>
              <strong>{priorityCount}</strong> active {priorityCount === 1 ? 'priority' : 'priorities'} —
              {' '}titles, ranks, statuses, time estimates, and allocation
            </span>
          </li>
          <li className="flex items-center gap-2">
            <CheckCircle2 size={13} className="text-emerald-500 flex-shrink-0" />
            <span>
              <strong>{themeCount}</strong> journal {themeCount === 1 ? 'theme' : 'themes'} from the last 30 days
              {' '}— themes only, not full text
            </span>
          </li>
          <li className="flex items-center gap-2">
            <CheckCircle2 size={13} className="text-emerald-500 flex-shrink-0" />
            <span>Bucket names, ordering, and target allocations</span>
          </li>
          <li className="flex items-center gap-2">
            <CheckCircle2 size={13} className="text-emerald-500 flex-shrink-0" />
            <span>Alignment score and alignment gaps</span>
          </li>
          {excludedCount > 0 && (
            <li className="flex items-center gap-2">
              <ShieldOff size={13} className="text-muted-foreground flex-shrink-0" />
              <span className="text-muted-foreground">
                <strong>{excludedCount}</strong> {excludedCount === 1 ? 'item' : 'items'} excluded at your request
              </span>
            </li>
          )}
        </ul>
      </div>

      {/* Never sent */}
      <div className="space-y-2">
        <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
          Never sent to the AI
        </p>
        <ul className="space-y-1.5 text-muted-foreground">
          <li className="flex items-center gap-2">
            <XCircle size={13} className="text-muted-foreground/60 flex-shrink-0" />
            Full journal entry text (themes only, unless you opt in)
          </li>
          <li className="flex items-center gap-2">
            <XCircle size={13} className="text-muted-foreground/60 flex-shrink-0" />
            Notes attached to priorities or life areas
          </li>
          <li className="flex items-center gap-2">
            <XCircle size={13} className="text-muted-foreground/60 flex-shrink-0" />
            Anything you've marked "Exclude from AI"
          </li>
        </ul>
      </div>

      {/* ai_excludable explanation */}
      <div className="space-y-2">
        <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground flex items-center gap-1.5">
          <ShieldOff size={11} />
          Exclude from AI
        </p>
        <p className="text-muted-foreground leading-relaxed">
          Any priority or journal entry you mark as "Excluded from AI" is removed from the
          context payload before the API call — enforced in code, not just in the prompt.
          The coach cannot reference, infer from, or be influenced by excluded items.
          Look for the <ShieldOff size={11} className="inline mx-0.5 relative -top-px" /> icon
          on each priority or journal entry to toggle this.
        </p>
      </div>

      {/* Where data goes */}
      <div className="space-y-3">
        <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
          Where your data goes
        </p>
        {llm?.provider === 'ollama' ? (
          <div className="flex gap-3 p-3 rounded-lg border border-border bg-muted/20">
            <Cpu size={16} className="text-emerald-500 flex-shrink-0 mt-0.5" />
            <div className="space-y-1">
              <p className="font-medium">Processed locally — {llm.model}</p>
              <p className="text-muted-foreground text-xs leading-relaxed">
                Everything stays on your machine. Nothing is sent to any server.
              </p>
            </div>
          </div>
        ) : (
          <div className="flex gap-3 p-3 rounded-lg border border-border bg-muted/20">
            <Server size={16} className="text-sky-500 flex-shrink-0 mt-0.5" />
            <div className="space-y-1">
              <p className="font-medium">
                Anthropic API {llm?.model ? `— ${llm.model}` : ''}
              </p>
              <p className="text-muted-foreground text-xs leading-relaxed">
                Your snapshot is sent to Anthropic's API to generate your letter.
                Anthropic does not train on API data. Zero data retention is configured —
                your input and the generated response are not stored on Anthropic's
                servers after the call completes.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Commitments */}
      <div className="space-y-2 border-t border-border pt-5">
        <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
          Our commitments
        </p>
        <ul className="space-y-1.5 text-muted-foreground text-xs leading-relaxed">
          <li className="flex items-start gap-2">
            <CheckCircle2 size={12} className="text-emerald-500 flex-shrink-0 mt-0.5" />
            Your data is never used to train AI models.
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 size={12} className="text-emerald-500 flex-shrink-0 mt-0.5" />
            Your data is never sold or shared with third parties.
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 size={12} className="text-emerald-500 flex-shrink-0 mt-0.5" />
            Journal text stays on your device unless you explicitly opt in.
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 size={12} className="text-emerald-500 flex-shrink-0 mt-0.5" />
            Everything you exclude is excluded in code, not just in the prompt.
          </li>
        </ul>
      </div>

    </div>
  )
}
